import React, { useState } from "react";
import { Link, type To } from "react-router-dom";
import {
	Button,
	Menu,
	MenuItem,
	makeStyles,
	Typography,
} from "@material-ui/core";
import { ExpandMore, ExpandLess } from "@material-ui/icons";
import { logbookNavbar, useHasPermission } from "@dexteel/mesf-core";

const useStyles = makeStyles((theme) => ({
	menuButton: {
		marginRight: theme.spacing(2),
		color: "white",
		display: "flex",
		alignItems: "center",
	},
	link: {
		textDecoration: "none",
		color: "inherit",
		display: "block",
		width: "100%",
	},
	menuItemText: {},
	iconMargin: {
		marginLeft: theme.spacing(0.5),
	},
}));

interface MenuItemConfig {
	label: string;
	to: To;
	permission?: string;
}

interface MenuConfig {
	id: string;
	label: string;
	items: MenuItemConfig[];
	permission?: string;
}

const menuConfigData: MenuConfig[] = [
	{
		id: "map",
		label: "Map",
		items: [
			{
				label: "Main Map",
				to: "/map/",
			},
		],
	},
	{
		id: "work-orders",
		label: "Work Orders",
		items: [
			{
				label: "Work Orders",
				to: "/work-orders",
			},
		],
	},
	{
		id: "task-list",
		label: "Task List",
		items: [
			{
				label: "Area Tasklist",
				to: "/task-list/area",
			},
			{
				label: "Downday Tasklist",
				to: "/task-list/downday",
			},
		],
	},
	{
		id: "templates",
		label: "Templates",
		items: [
			{
				label: "Templates",
				to: "/templates",
			},
		],
	},
	{
		id: "forms",
		label: "Forms",
		items: [
			{
				label: "Forms",
				to: "/forms",
			},
		],
	},
	...logbookNavbar,
	{
		id: "documents",
		label: "Documents",
		items: [
			{
				label: "Search Documents",
				to: "/search-documents",
			},
			{
				label: "Browser Documents",
				to: "/browser-documents",
			},
		],
	},
];

const CustomNavbar = () => {
	const hasPermissionTo = useHasPermission();
	const classes = useStyles();
	const [anchorElements, setAnchorElements] = useState<{
		[key: string]: HTMLElement | null;
	}>({});

	const handleMenuOpen = (
		event: React.MouseEvent<HTMLButtonElement>,
		menuId: string,
	) => {
		setAnchorElements((prevAnchors) => ({
			...prevAnchors,
			[menuId]: event.currentTarget,
		}));
	};

	const handleMenuClose = (menuId: string) => {
		setAnchorElements((prevAnchors) => ({
			...prevAnchors,
			[menuId]: null,
		}));
	};

	return (
		<>
			{menuConfigData.map((menu) => {
				if (menu.permission && !hasPermissionTo(menu.permission)) {
					return null;
				}

				const currentAnchorEl = anchorElements[menu.id] || null;
				const isMenuOpen = Boolean(currentAnchorEl);

				if (menu.items.length === 1) {
					const singleItem = menu.items[0];

					if (
						singleItem.permission &&
						!hasPermissionTo(singleItem.permission)
					) {
						return null;
					}

					return (
						<Button
							key={menu.id}
							component={Link}
							to={singleItem.to}
							className={classes.menuButton}
						>
							{menu.label}
						</Button>
					);
				}

				return (
					<React.Fragment key={menu.id}>
						<Button
							aria-controls={`${menu.id}-menu`}
							aria-haspopup="true"
							onClick={(event) => handleMenuOpen(event, menu.id)}
							className={classes.menuButton}
						>
							{menu.label}
							{isMenuOpen ? (
								<ExpandLess className={classes.iconMargin} />
							) : (
								<ExpandMore className={classes.iconMargin} />
							)}
						</Button>
						<Menu
							id={`${menu.id}-menu`}
							anchorEl={currentAnchorEl}
							keepMounted
							open={isMenuOpen}
							onClose={() => handleMenuClose(menu.id)}
							getContentAnchorEl={null}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "center",
							}}
							transformOrigin={{
								vertical: "top",
								horizontal: "center",
							}}
						>
							{menu.items
								.filter(
									(item) =>
										!item.permission || hasPermissionTo(item.permission),
								)
								.map((item, index) => (
									<MenuItem
										key={`${menu.id}-item-${index}`}
										component={Link}
										to={item.to}
										onClick={() => handleMenuClose(menu.id)}
										className={classes.link}
									>
										<Typography className={classes.menuItemText}>
											{item.label}
										</Typography>
									</MenuItem>
								))}
						</Menu>
					</React.Fragment>
				);
			})}
		</>
	);
};

export { CustomNavbar };
